import { isTokenExpired, refreshToken } from "../services/authServices";
import { API_URL } from "../config/endpoints";
import { PropertiesResponse } from "../types/properties/propertiesResponse";
import { useEffect, useMemo, useState } from "react";

export const useProperties = (
  filters: Record<string, string | number | boolean | null> = {},
  uid?: string
) => {
  const [properties, setProperties] = useState<PropertiesResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const sanitizedFilters = useMemo(() => {
    return Object.fromEntries(
      Object.entries(filters)
        .filter(([_, value]) => value !== null && value !== undefined)
        .map(([key, value]) => [key, String(value)]) // Ensure all values are strings
    );
  }, [filters]);

  const queryParams = useMemo(
    () => new URLSearchParams(sanitizedFilters).toString(),
    [sanitizedFilters]
  );
  const apiUrl = useMemo(
    () =>
      `${API_URL}/guest/properties/${
        uid ? `${uid}/availability` : ""
      }?${queryParams}`,
    [uid, queryParams]
  );

  useEffect(() => {
    const fetchProperties = async () => {
      setLoading(true);
      setError(null);

      try {
        let token = localStorage.getItem("token");
        if (!token || isTokenExpired(token)) {
          token = await refreshToken();
        }

        const response = await fetch(apiUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) throw new Error("Failed to fetch properties");

        const data = await response.json();
        setProperties(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unknown error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, [apiUrl]);

  return { properties, loading, error };
};
